import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Palette, FileImage, Sparkles, Type, Frame } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";

interface CampoNombre {
  activo: boolean;
  label: string;
  placeholder: string;
  maxCaracteres: number;
}

interface CubiertaConfig {
  tieneArgollas: boolean;
  urlConArgollas: string;
  urlSinArgollas: string;
}

interface PersonalizacionConfig {
  permiteEstampados?: boolean;
  categoriaEstampados?: 'infantil' | 'literario' | 'general';
  permiteImagenPersonalizada?: boolean;
  campoNombre?: CampoNombre;
  cubierta?: CubiertaConfig;
}

interface EditorPersonalizacionProps {
  value: PersonalizacionConfig;
  onChange: (config: PersonalizacionConfig) => void;
}

export const EditorPersonalizacion = ({ value, onChange }: EditorPersonalizacionProps) => {
  const updateField = <K extends keyof PersonalizacionConfig>(
    field: K,
    newValue: PersonalizacionConfig[K]
  ) => {
    console.log(`🎨 [EditorPersonalizacion] updateField: ${String(field)} =`, newValue);
    const newConfig = {
      ...value,
      [field]: newValue,
    };
    console.log('🎨 [EditorPersonalizacion] Nueva configuración después de updateField:', newConfig);
    onChange(newConfig);
  };

  const updateCampoNombre = <K extends keyof CampoNombre>(
    field: K,
    newValue: CampoNombre[K]
  ) => {
    onChange({
      ...value,
      campoNombre: {
        ...value.campoNombre,
        [field]: newValue,
      } as CampoNombre,
    });
  };

  return (
    <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50/50 to-pink-50/50">
      <CardHeader className="bg-gradient-to-br from-purple-100/80 to-pink-100/80">
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-purple-600" />
          <div className="flex-1">
            <div className="font-bold text-purple-900">Configuración de Personalización</div>
            <div className="text-sm font-normal text-purple-700 mt-1">
              Define qué opciones de personalización tendrá este producto
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        {/* Alerta informativa */}
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-blue-800">
            Estas opciones solo aplican cuando el cliente hace click en "Personalizar" (no en "Comprar")
          </AlertDescription>
        </Alert>

        {/* Selector de Tipo de Producto (Presets) */}
        <div className="p-4 border-2 rounded-lg bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
          <Label htmlFor="tipoProducto" className="text-base font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Tipo de Producto Personalizable (Presets)
          </Label>
          <p className="text-sm text-muted-foreground mt-1 mb-3">
            Aplica configuraciones predefinidas según el tipo de producto
          </p>
          <Select
            value={value.categoriaEstampados || 'general'}
            onValueChange={(val: 'infantil' | 'literario' | 'general') => {
              // Aplicar preset completo según la selección
              const presets = {
                general: {
                  categoriaEstampados: 'general' as const,
                  permiteEstampados: true,
                  campoNombre: {
                    activo: true,
                    label: 'Nombre personalizado',
                    placeholder: 'Ingresa un nombre',
                    maxCaracteres: 50
                  }
                },
                infantil: {
                  categoriaEstampados: 'infantil' as const,
                  permiteEstampados: true,
                  campoNombre: {
                    activo: true,
                    label: 'Nombre del bebé',
                    placeholder: 'Ej: Bebé Lucas',
                    maxCaracteres: 30
                  }
                },
                literario: {
                  categoriaEstampados: 'literario' as const,
                  permiteEstampados: true,
                  campoNombre: {
                    activo: true,
                    label: 'Tu nombre',
                    placeholder: 'Ej: María García',
                    maxCaracteres: 30
                  }
                }
              };

              // Aplicar preset manteniendo cubierta
              const newConfig = {
                ...value,
                ...presets[val],
                cubierta: value.cubierta // Mantener configuración de cubierta
              };
              console.log('🎨 [EditorPersonalizacion] Aplicando preset:', val);
              console.log('🎨 [EditorPersonalizacion] Nueva configuración:', newConfig);
              onChange(newConfig);
            }}
          >
            <SelectTrigger id="tipoProducto" className="mt-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="general">
                <div className="flex flex-col">
                  <span className="font-medium">✨ General - Agendas 2026</span>
                  <span className="text-xs text-muted-foreground">Para agendas estándar y planificadores</span>
                </div>
              </SelectItem>
              <SelectItem value="infantil">
                <div className="flex flex-col">
                  <span className="font-medium">🍼 Infantil - Agenda Pediátrica</span>
                  <span className="text-xs text-muted-foreground">Para bebés, control médico, vacunas</span>
                </div>
              </SelectItem>
              <SelectItem value="literario">
                <div className="flex flex-col">
                  <span className="font-medium">📚 Literario - Diario de Lectura</span>
                  <span className="text-xs text-muted-foreground">Para lectores, reseñas, libros</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1">
            <span>💡</span>
            <span>
              Al seleccionar un tipo, se aplicarán automáticamente los textos y configuraciones apropiadas.
              Puedes personalizarlos después en las secciones de abajo.
            </span>
          </p>
        </div>

        {/* Estampados de Portada */}
        <div className="space-y-4 p-4 border-2 rounded-lg bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-purple-600" />
              <div>
                <Label className="text-base font-semibold">Estampados de Portada</Label>
                <p className="text-sm text-muted-foreground">
                  Permitir seleccionar diseños predefinidos para la portada
                </p>
              </div>
            </div>
            <Switch
              checked={value.permiteEstampados ?? true}
              onCheckedChange={(checked) => updateField('permiteEstampados', checked)}
            />
          </div>

          {value.permiteEstampados && (
            <div className="pt-3 border-t">
              <p className="text-xs text-muted-foreground">
                La categoría de estampados se configura automáticamente según el tipo de producto seleccionado arriba.
                Los estampados se gestionan desde Admin → Estampados y Hojas
              </p>
            </div>
          )}
        </div>

        {/* Imagen Personalizada */}
        <div className="flex items-center justify-between p-4 border-2 rounded-lg bg-white">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-purple-600" />
            <div>
              <Label className="text-base font-semibold">Imagen Personalizada</Label>
              <p className="text-sm text-muted-foreground">
                Permitir al cliente subir su propia imagen
              </p>
            </div>
          </div>
          <Switch
            checked={value.permiteImagenPersonalizada ?? true}
            onCheckedChange={(checked) => updateField('permiteImagenPersonalizada', checked)}
          />
        </div>

        {/* Campo de Nombre */}
        <div className="space-y-4 p-4 border-2 rounded-lg bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Type className="h-5 w-5 text-purple-600" />
              <div>
                <Label className="text-base font-semibold">Campo de Nombre/Texto</Label>
                <p className="text-sm text-muted-foreground">
                  Permitir agregar un nombre o texto personalizado
                </p>
              </div>
            </div>
            <Switch
              checked={value.campoNombre?.activo ?? true}
              onCheckedChange={(checked) => updateCampoNombre('activo', checked)}
            />
          </div>

          {value.campoNombre?.activo && (
            <div className="pt-3 border-t space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="label">Etiqueta del campo</Label>
                  <Input
                    id="label"
                    value={value.campoNombre?.label || 'Nombre personalizado'}
                    onChange={(e) => updateCampoNombre('label', e.target.value)}
                    placeholder="Ej: Nombre del bebé"
                    className="mt-2"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Texto que verá el usuario
                  </p>
                </div>

                <div>
                  <Label htmlFor="placeholder">Placeholder</Label>
                  <Input
                    id="placeholder"
                    value={value.campoNombre?.placeholder || 'Ingresa un nombre'}
                    onChange={(e) => updateCampoNombre('placeholder', e.target.value)}
                    placeholder="Ej: Bebé Lucas"
                    className="mt-2"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Ejemplo de texto
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="maxCaracteres">Máximo de caracteres</Label>
                <Input
                  id="maxCaracteres"
                  type="number"
                  min="1"
                  max="100"
                  value={value.campoNombre?.maxCaracteres || 50}
                  onChange={(e) =>
                    updateCampoNombre('maxCaracteres', parseInt(e.target.value) || 50)
                  }
                  className="mt-2 max-w-xs"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Límite de caracteres permitidos (1-100)
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Configuración de Cubierta/Lomo */}
        <div className="space-y-4 p-4 border-2 rounded-lg bg-white">
          <div className="flex items-center gap-3 mb-4">
            <Frame className="h-5 w-5 text-purple-600" />
            <div>
              <Label className="text-base font-semibold">Tipo de Cubierta/Lomo</Label>
              <p className="text-sm text-muted-foreground">
                Define si la agenda usa lomo con argollas o sin argollas
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="tipoLomo">Tipo de Lomo</Label>
              <Select
                value={
                  value.cubierta?.tieneArgollas !== undefined
                    ? (value.cubierta.tieneArgollas ? 'con-argollas' : 'sin-argollas')
                    : 'con-argollas'
                }
                onValueChange={(val) => {
                  const nuevaConfig = {
                    ...value,
                    cubierta: {
                      tieneArgollas: val === 'con-argollas',
                      urlConArgollas: value.cubierta?.urlConArgollas || 'https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/general/agenda-base-con-argollas.png',
                      urlSinArgollas: value.cubierta?.urlSinArgollas || 'https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/general/agenda-base-sin-argollas.png'
                    }
                  };
                  console.log('Configuración de cubierta actualizada:', nuevaConfig.cubierta);
                  onChange(nuevaConfig);
                }}
              >
                <SelectTrigger id="tipoLomo" className="mt-2">
                  <SelectValue placeholder="Selecciona el tipo de lomo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="con-argollas">
                    <div className="flex flex-col">
                      <span className="font-medium">Con Argollas</span>
                      <span className="text-xs text-muted-foreground">Lomo con anillos/argollas visibles</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="sin-argollas">
                    <div className="flex flex-col">
                      <span className="font-medium">Sin Argollas</span>
                      <span className="text-xs text-muted-foreground">Lomo simple sin argollas</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Preview de las URLs configuradas */}
            <div className="pt-3 border-t space-y-2">
              <p className="text-xs font-medium text-muted-foreground">URLs de plantillas configuradas:</p>
              <div className="text-xs space-y-1 font-mono bg-gray-50 p-2 rounded">
                <p className="truncate">
                  <span className="font-semibold">Con argollas:</span>{' '}
                  {value.cubierta?.urlConArgollas || 'Por defecto'}
                </p>
                <p className="truncate">
                  <span className="font-semibold">Sin argollas:</span>{' '}
                  {value.cubierta?.urlSinArgollas || 'Por defecto'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
